import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { preOrderService } from '../../services/preOrderService';
import type { PreOrder } from '../../types/catalog';
import { PRE_ORDER_STATUS_COLORS, PRE_ORDER_STATUS_LABELS } from './preOrderStatus';
import { formatDateTime } from '../../utils/dateTimeUtils';

const formatPrice = (value: number): string => `$${value.toFixed(2)}`;

const errorMessage = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: unknown } }).response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

export default function PreOrderDetailPage() {
  const { id, preOrderId } = useParams<{ id: string; preOrderId: string }>();
  const navigate = useNavigate();

  const [preOrder, setPreOrder] = useState<PreOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [editedQuantities, setEditedQuantities] = useState<Record<number, number> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setPreOrder(await preOrderService.getById(parseInt(preOrderId ?? '')));
      } catch (e) {
        setError(errorMessage(e, 'Не вдалося завантажити передзамовлення'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [preOrderId]);

  const runAction = async (action: (orderId: number) => Promise<PreOrder>, confirmText?: string) => {
    if (!preOrder || (confirmText && !window.confirm(confirmText))) return;

    setActing(true);
    try {
      setPreOrder(await action(preOrder.id));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Не вдалося виконати дію'));
    } finally {
      setActing(false);
    }
  };

  const isActive = preOrder?.status === 'NEW' || preOrder?.status === 'CONFIRMED';

  const handleStartEditing = () => {
    if (!preOrder) return;
    setEditedQuantities(Object.fromEntries(preOrder.items.map((item) => [item.productId, item.quantity])));
  };

  const handleCancelEditing = () => {
    setEditedQuantities(null);
  };

  const handleQuantityChange = (productId: number, value: string) => {
    setEditedQuantities((prev) => (prev ? { ...prev, [productId]: parseInt(value) || 0 } : prev));
  };

  const handleRemoveItem = (productId: number) => {
    setEditedQuantities((prev) => {
      if (!prev) return prev;
      const rest = { ...prev };
      delete rest[productId];
      return rest;
    });
  };

  const handleSaveItems = async () => {
    if (!preOrder || !editedQuantities) return;

    setActing(true);
    try {
      const items = Object.entries(editedQuantities).map(([productId, quantity]) => ({
        productId: parseInt(productId),
        quantity,
      }));
      setPreOrder(await preOrderService.updateItems(preOrder.id, items));
      setEditedQuantities(null);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Не вдалося зберегти позиції'));
    } finally {
      setActing(false);
    }
  };

  const handleConvert = () => {
    navigate(`/companies/${id}/preorders/${preOrderId}/convert`);
  };

  const canSaveItems = editedQuantities !== null
    && Object.keys(editedQuantities).length > 0
    && Object.values(editedQuantities).every((quantity) => quantity >= 1 && quantity <= 9999);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 800, mx: 'auto', width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${id}/preorders`)}
          size="small"
        >
          До передзамовлень
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          {preOrder ? `Передзамовлення №${preOrder.id}` : 'Передзамовлення'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {preOrder && (
        <>
          <Box sx={{ mb: 1 }}>
            <Chip
              label={PRE_ORDER_STATUS_LABELS[preOrder.status]}
              color={PRE_ORDER_STATUS_COLORS[preOrder.status]}
            />
          </Box>

          <Typography variant="body1" color="text.secondary">
            Клієнт: {preOrder.clientName ? `${preOrder.clientName}, ` : ''}{preOrder.clientEmail}{preOrder.clientPhone ? `, ${preOrder.clientPhone}` : ''}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Каталог: {preOrder.catalogName}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Створено: {formatDateTime(preOrder.createdDate)}
          </Typography>

          <List>
            {preOrder.items
              .filter((item) => editedQuantities === null || item.productId in editedQuantities)
              .map((item) => (
                <ListItem key={item.productId} divider>
                  <ListItemText
                    primary={item.productName}
                    secondary={`${item.productCode ? `${item.productCode} · ` : ''}${editedQuantities === null ? `${item.quantity} × ` : '× '}${formatPrice(item.unitPrice)}`}
                  />
                  {editedQuantities === null ? (
                    <Typography variant="body1">{formatPrice(item.quantity * item.unitPrice)}</Typography>
                  ) : (
                    <>
                      <TextField
                        type="number"
                        size="small"
                        label="Кількість"
                        value={editedQuantities[item.productId] || ''}
                        onChange={(e) => handleQuantityChange(item.productId, e.target.value)}
                        slotProps={{ htmlInput: { min: 1, max: 9999 } }}
                        sx={{ width: 110, mr: 1 }}
                      />
                      <IconButton
                        color="error"
                        aria-label="Прибрати позицію"
                        onClick={() => handleRemoveItem(item.productId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </ListItem>
              ))}
          </List>
          <Typography variant="h6" sx={{ textAlign: 'right', mt: 1, mb: 3 }}>
            Разом: {formatPrice(preOrder.total)}
          </Typography>

          {preOrder.activityGroupIds.length > 0 && (
            <Typography variant="body1" sx={{ mb: 3 }}>
              Реалізовано в активностях:{' '}
              {preOrder.activityGroupIds.map((groupId, index) => (
                <span key={groupId}>
                  {index > 0 && ', '}
                  <Link component={RouterLink} to={`/companies/${id}/activities/group/${groupId}/edit`}>№{groupId}</Link>
                </span>
              ))}
            </Typography>
          )}

          {editedQuantities !== null ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="contained" disabled={acting || !canSaveItems} onClick={handleSaveItems}>
                Зберегти позиції
              </Button>
              <Button variant="outlined" disabled={acting} onClick={handleCancelEditing}>
                Скасувати редагування
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {preOrder.status === 'NEW' && (
                <Button
                  variant="contained"
                  disabled={acting}
                  onClick={() => runAction(preOrderService.confirm)}
                >
                  Підтвердити
                </Button>
              )}
              {isActive && (
                <Button variant="contained" color="success" disabled={acting} onClick={handleConvert}>
                  Реалізувати в активності
                </Button>
              )}
              {isActive && (
                <Button variant="outlined" disabled={acting} onClick={handleStartEditing}>
                  Редагувати позиції
                </Button>
              )}
              {isActive && (
                <Button
                  variant="outlined"
                  disabled={acting}
                  onClick={() => runAction(
                    preOrderService.closeWithoutActivity,
                    'Закрити без активності? Склад і маржа для цього замовлення не відстежуватимуться.',
                  )}
                >
                  Закрити без активності
                </Button>
              )}
              {isActive && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={acting}
                  onClick={() => runAction(preOrderService.cancel, 'Скасувати це передзамовлення?')}
                >
                  Скасувати
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
